import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DecorKindSchema, type DecorKind } from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';

/**
 * Экран 2.2.2 — выбор метода подбора декора (PR-FLOW, ADR-0043).
 *
 * Стоит между фильтр-экраном (`/decors/:kind`, DecorsFilter) и ветками подбора.
 * Три вертикальные кнопки-возможности (PRD §5):
 *   1. «Подобрать декор по фото»  → `/decors/:kind/photo`    (фото/PDF подбор).
 *   2. «Найти декор по названию»  → `/decors/:kind/search`   (живой поиск, ADR-0067).
 *   3. «Полистай каталог»         → `/decors/:kind/catalog`  (список по фильтру).
 *
 * Выбранные фильтры (subcategory/brand/type) приходят как verbatim `location.search`
 * и переносятся вперёд БЕЗ изменений (persistent intent, R3; Решение №1 = ДА:
 * подбор по фото ищет ВНУТРИ фильтра). Формат — повтор ключа, идентичный
 * useCatalogFilters (`?subcategories=..&brands=..&types=..`). Метод-пикер не
 * парсит фильтры — просто пробрасывает строку в photo/catalog.
 *
 * Дочерний экран KindPicker'а → VideoButton НЕ дублируется (P-040; PRD 2.2.3:
 * «на дочерних экранах функции кнопка не дублируется»). Решение Aibek 2026-06-01.
 *
 * Без async-состояния → single-file (как Block 2 KindPicker), без
 * container/presenter split (ADR-0005 split нужен там, где есть fetch/state).
 */
export function MethodPicker() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ kind: string }>();
  const kindParse = DecorKindSchema.safeParse(params.kind);
  // Silent fallback при неизвестном kind — единый паттерн блока (DecorsCatalog,
  // PhotoPicker используют тот же fallback на 'facade').
  const kind: DecorKind = kindParse.success ? kindParse.data : 'facade';
  // Verbatim фильтр-строка переносится дальше без изменений (persistent intent, R3).
  const search = location.search;
  const base = `/decors/${encodeURIComponent(kind)}`;

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Как подобрать декор?" />

      <div className="flex-1 flex flex-col gap-4 p-6">
        <MethodButton
          label="Подобрать декор по фото"
          onClick={() => navigate(`${base}/photo${search}`)}
        />
        <MethodButton
          label="Найти декор по названию"
          onClick={() => navigate(`${base}/search${search}`)}
        />
        <MethodButton
          label="Полистай каталог"
          onClick={() => navigate(`${base}/catalog${search}`)}
        />
      </div>

      <BottomBar />
    </main>
  );
}

interface MethodButtonProps {
  label: string;
  sublabel?: string;
  onClick?: () => void;
  disabled?: boolean;
}

function MethodButton({ label, sublabel, onClick, disabled = false }: MethodButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={sublabel ? `${label} (${sublabel})` : label}
      className={
        'min-h-tap-chip-lg w-full rounded-xl bg-card border-[1.5px] border-accent ' +
        'text-ink font-display text-h2 shadow-card flex flex-col items-center justify-center gap-1 ' +
        'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90 ' +
        'disabled:opacity-50 disabled:active:opacity-50'
      }
      style={{ minHeight: 120 }}
    >
      <span>{label}</span>
      {sublabel && <span className="text-ink text-base font-body opacity-70">{sublabel}</span>}
    </button>
  );
}
